import type { SupportedLanguage } from "./language-preference.ts";

// Numbers and dates the way the active language writes them: "1,234.5" and
// "Oct 24, 2026" in English, "1234,5" and "24 oct 2026" in Spanish. A screen
// reads them through `useFormat` (use-format.ts); a pure `model/` function
// that formats takes the `Formats` as an argument, the way it takes `t`.

export type DateInput = Date | string | number;

export type Formats = {
  readonly number: (value: number, options?: Intl.NumberFormatOptions) => string;
  readonly date: (value: DateInput, options?: Intl.DateTimeFormatOptions) => string;
  readonly dateTime: (value: DateInput, options?: Intl.DateTimeFormatOptions) => string;
  readonly time: (value: DateInput, options?: Intl.DateTimeFormatOptions) => string;
};

/** The `Intl` locale a shipped language formats in. */
export function localeOf(language: SupportedLanguage): string {
  return language;
}

const asDate = (value: DateInput): Date => (value instanceof Date ? value : new Date(value));

/** Formatters for one shipped language; each `options` replaces the default style whole. */
export function formatsFor(language: SupportedLanguage): Formats {
  const locale = localeOf(language);
  return {
    number: (value, options) => new Intl.NumberFormat(locale, options).format(value),
    date: (value, options = { dateStyle: "medium" }) =>
      new Intl.DateTimeFormat(locale, options).format(asDate(value)),
    dateTime: (value, options = { dateStyle: "medium", timeStyle: "short" }) =>
      new Intl.DateTimeFormat(locale, options).format(asDate(value)),
    time: (value, options = { hour: "2-digit", minute: "2-digit" }) =>
      new Intl.DateTimeFormat(locale, options).format(asDate(value)),
  };
}
